/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ReactNode } from 'react';
import { PopoverProps } from 'antd/lib/popover';
import { ControlComponentProps } from '@superset-ui/chart-controls';

export enum COMPARATOR {
  NONE = 'None',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_OR_EQUAL = '≥',
  LESS_OR_EQUAL = '≤',
  EQUAL = '=',
  NOT_EQUAL = '≠',
  BETWEEN = '< x <',
  BETWEEN_OR_EQUAL = '≤ x ≤',
  BETWEEN_OR_LEFT_EQUAL = '≤ x <',
  BETWEEN_OR_RIGHT_EQUAL = '< x ≤',
}

export const MULTIPLE_VALUE_COMPARATORS = [
  COMPARATOR.BETWEEN,
  COMPARATOR.BETWEEN_OR_EQUAL,
  COMPARATOR.BETWEEN_OR_LEFT_EQUAL,
  COMPARATOR.BETWEEN_OR_RIGHT_EQUAL,
];

// 新增自定义颜色接口
export interface CustomColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type ConditionalFormattingConfig = {
  operator?: COMPARATOR;
  targetValue?: number;
  targetValueLeft?: number;
  targetValueRight?: number;
  column?: string;
  colorScheme?: string;
  customColor?: CustomColor;
  // 新增: 进度条展现形式（复选框）
  showProgressBar?: boolean;
  // 新增: 启用色阶效果（复选框）
  enableColorScale?: boolean;
  // 新增: 单元格下标配置
  showSubscript?: boolean; // 是否显示下标
  achievedText?: string; // 差值前缀，如"达标"、"完成"等
  showDifference?: boolean; // 是否显示差值
  differenceColor?: string; // 差值颜色（十六进制颜色值或 RGB 对象）
  // 新增: 过滤0值（复选框）
  filterZero?: boolean; // 是否过滤0值（不参与条件判定和差值计算）
  // 新增: 差值目标值（独立字段，用于计算差值，与条件判断的目标值分离）
  differenceTargetValue?: number; // 差值计算的目标值（数字类型，可以是小数或整数）
  // 新增: 差值计算方式（下拉框）
  differenceCalculation?: 'actual_minus_target' | 'target_minus_actual'; // 差值计算方式
  // 新增: 进度条样式（下拉框）
  progressBarStyle?: 'striped' | 'classic'; // 进度条样式：条纹 或 经典
  // 新增: 进度条宽度（整数输入）
  progressBarWidth?: number; // 进度条宽度（像素值）
  // 新增: 图标集类型（下拉框）
  iconSetType?: 'arrows3' | 'arrows4' | 'arrows5' | 'traffic' | 'stars'; // 图标集类型
  // 新增: 图标集展现（复选框）
  showIconSet?: boolean; // 是否显示图标集
  // 新增: 迷你柱状图展现（复选框）
  showMiniBarChart?: boolean; // 是否显示迷你柱状图
  // 新增: 迷你柱状图样式（下拉框）
  miniBarChartStyle?: 'vertical' | 'horizontal'; // 迷你柱状图样式：垂直 或 水平
  // 新增: 迷你柱状图宽度（整数输入）
  miniBarChartWidth?: number; // 迷你柱状图宽度（像素值）
  // 新增: 迷你柱状图高度（整数输入）
  miniBarChartHeight?: number; // 迷你柱状图高度（像素值）
  // 新增: 圆环/饼图进度（复选框）
  showDonutProgress?: boolean; // 是否显示圆环进度图条
  // 新增: 圆环直径（整数输入）
  donutSize?: number; // 圆环直径（像素值）
  // 新增: 圆环厚度（整数输入）
  donutThickness?: number; // 圆环厚度（像素值）
  // 新增: 展现方式（下拉框）
  displayType?:
    | 'none'
    | 'progressBar'
    | 'miniBarChart'
    | 'donutProgress'
    | 'iconSet'
    | 'miniPieChart'
    | 'bulletChart'
    | 'button'; // 展现方式
  // 新增: 按钮样式配置
  buttonStyle?: 'solid' | 'outlined' | 'tag'; // 按钮样式：实心、描边、标签
  buttonBgColor?: string; // 按钮背景颜色（仅在实心/标签样式时有效，默认使用颜色方案）
  buttonRadius?: number; // 按钮圆角（像素值），默认4
  buttonPadding?: number; // 按钮内边距（像素值），默认4
  buttonBorderWidth?: number; // 按钮边框宽度（像素值），默认1
  // 新增: 迷你饼图展现（复选框）
  showMiniPieChart?: boolean; // 是否显示迷你饼图
  // 新增: 迷你饼图直径（整数输入）
  miniPieChartSize?: number; // 迷你饼图直径（像素值），默认 24
  // 新增: 迷你饼图是否显示中心百分比
  miniPieChartShowPercentage?: boolean; // 是否在饼图中心显示百分比
  // 新增: 子弹图展现（复选框）
  showBulletChart?: boolean; // 是否显示子弹图
  // 新增: 子弹图宽度（整数输入）
  bulletChartWidth?: number; // 子弹图宽度（像素值），默认 80
  // 新增: 子弹图高度（整数输入）
  bulletChartHeight?: number; // 子弹图高度（像素值），默认 16
  // 新增: 子弹图目标值颜色
  bulletChartTargetColor?: string; // 目标线颜色，默认 #333
  // 新增: 子弹图目标值是否展示
  bulletChartShowTarget?: boolean; // 是否展示目标值，默认 false
};

export type ConditionalFormattingControlProps = ControlComponentProps<
  ConditionalFormattingConfig[]
> & {
  columnOptions: { label: string; value: string }[];
  removeIrrelevantConditions: boolean;
  verboseMap: Record<string, string>;
  label: string;
  description: string;
  // 是否显示下标相关配置（仅普通表格使用）
  showSubscriptOptions?: boolean;
  // 是否显示进度条和色阶相关配置（仅特定图表插件使用）
  showProgressOptions?: boolean;
};

export type FormattingPopoverProps = PopoverProps & {
  columns: { label: string; value: string }[];
  onChange: (value: ConditionalFormattingConfig) => void;
  config?: ConditionalFormattingConfig;
  title: string;
  children: ReactNode;
  // 是否显示下标相关配置（仅普通表格使用）
  showSubscriptOptions?: boolean;
  // 是否显示进度条和色阶相关配置（仅特定图表插件使用）
  showProgressOptions?: boolean;
};
